// Centralised axios setup
// - Global 401 interceptor: clears the stale session and redirects to /auth.
// - Auto-retry once for network blips (no response) and 502/503/504 gateway errors.
// - Cleaner error messages (used by toast.error fallbacks across the app).
// - Feb 23 2026 · Owner "Enter Hotel" inspection mode: when the Owner has
//   entered a hotel from the Owner dashboard, we stamp EVERY request
//   with `X-Owner-View-As-Hotel-Id` so the backend serves and mutates
//   that hotel's data.
//   Mar 01 2026 — Read-only was removed. Owner Inspection is now FULL
//   EDIT: reads and writes both scope to the inspected hotel via the
//   `_resolve_hotel_strict` helper on the backend. See `auth.py`.
import axios from 'axios';
import { toast } from 'sonner';

// Session-storage keys owned by the Owner inspection mode.
export const OWNER_INSPECT_HOTEL_ID_KEY = 'owner_inspect_hotel_id';
export const OWNER_INSPECT_HOTEL_NAME_KEY = 'owner_inspect_hotel_name';

const readOwnerInspectHotelId = () => {
  try {
    return sessionStorage.getItem(OWNER_INSPECT_HOTEL_ID_KEY) || '';
  } catch (_) {
    return '';
  }
};

let isRedirectingTo401 = false;

// Public routes where a visitor is NOT authenticated by design. A 401 on these
// pages must NOT surface a "session expired" toast or force-redirect to /auth
// — there was never a logged-in session to expire.
//   /               → WelcomePage
//   /explore …      → Visitor experience (no auth)
//   /auth …         → Sign in / Register (login form itself)
//   /order …        → Public Stripe order receipt (visitor purchases)
const isPublicPath = (pathname = '') => (
  pathname === '/' ||
  pathname.startsWith('/explore') ||
  pathname.startsWith('/auth') ||
  pathname.startsWith('/order')
);

export const setupAxios = () => {
  // Mark any request originating from the auth endpoints so we don't fire the
  // global 401 redirect handler while the user is literally trying to log in.
  const isAuthEndpoint = (url = '') =>
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/auth/me');

  // Owner "Enter Hotel" inspection · stamp header on EVERY request
  // (GET + POST + PUT + PATCH + DELETE). Owner is the highest-level
  // role, so when inspecting a hotel it has full access to operate
  // and edit that hotel. Backend `_resolve_hotel_strict` scopes every
  // read AND write to the inspected hotel_id.
  axios.interceptors.request.use((config) => {
    const hid = readOwnerInspectHotelId();
    if (hid) {
      config.headers = config.headers || {};
      config.headers['X-Owner-View-As-Hotel-Id'] = hid;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const cfg = error.config || {};
      const status = error.response?.status;
      const url = cfg.url || '';
      const pathname = (typeof window !== 'undefined' && window.location)
        ? window.location.pathname
        : '';

      // 401 on a PUBLIC visitor path — silently drop any stale token, do NOT
      // toast "session expired", do NOT redirect. Anonymous visitors have no
      // session to expire.
      if (status === 401 && isPublicPath(pathname)) {
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
        } catch (_) {}
        return Promise.reject(error);
      }

      // 401 — session expired or stale token. Clear and redirect to /auth.
      if (status === 401 && !isAuthEndpoint(url) && !isRedirectingTo401) {
        isRedirectingTo401 = true;
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
        } catch (_) {}
        toast.error('Your session expired — please log in again', { duration: 4000 });
        // Defer redirect so the toast can render
        setTimeout(() => {
          isRedirectingTo401 = false;
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
            window.location.href = '/auth';
          }
        }, 600);
        return Promise.reject(error);
      }

      // 413 — payload too large (usually huge base64 images)
      if (status === 413) {
        toast.error('That payload is too large — try a smaller image (under 1 MB).');
        return Promise.reject(error);
      }

      // Auto-retry once for transient errors (network blip or 5xx gateway)
      const transient = !error.response || [502, 503, 504].includes(status);
      if (transient && !cfg.__retried && (cfg.method || 'get').toLowerCase() !== 'post') {
        // Only retry idempotent verbs to avoid double-creates on POST.
        cfg.__retried = true;
        await new Promise((r) => setTimeout(r, 700));
        return axios(cfg);
      }

      return Promise.reject(error);
    }
  );
};

// Helper: produce a clean human-readable message from any axios error
export const errMsg = (err, fallback = 'Something went wrong') => {
  if (!err) return fallback;
  if (!err.response) return err.message || 'Network error — check your connection';
  const detail = err.response.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return err.response.statusText || fallback;
};
